import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Input, message } from 'antd';
import { FileOutlined, SendOutlined } from '@ant-design/icons';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Button } from '@/components/ui/Button';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { PendingAttachmentTile, PendingLinkWrap } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import { LINK_URL_EXAMPLE_HELP, normalizeUrlOnFirstType, validateLinkUrlInput } from '@utils/linkUrlValidation.jsx';
import { contentToApiMentions } from '@utils/richTextMentions';
import { buildTaskDescription, truncateFileName } from '@hooks/projects/projectHelpers';
import { useProjectTaskBlockers } from '@hooks/projects/useProjectBlockers';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ModalChromeHeading,
    ModalBlockLabel,
    PanelSectionLabel,
} from '@/pages/inside/tenant/style';

function makePendingFileEntries(files) {
    if (!files?.length) return [];
    return Array.from(files).map((file, i) => ({
        id: `task-blocker-${Date.now()}-${i}`,
        file,
        name: file.name,
        type: file.type,
        preview: file.type?.startsWith?.('image/') ? URL.createObjectURL(file) : null,
    }));
}

export default function TaskBlockerCreateModal({
    projectId,
    taskId,
    blocker,
    open,
    onCancel,
    onCreated,
    mentionUsers,
    mentionMilestones,
    mentionSprints,
}) {
    const [form] = Form.useForm();

    const [pendingFiles, setPendingFiles] = useState([]);
    const [pendingLinks, setPendingLinks] = useState([]);

    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkModalForm] = Form.useForm();
    const linkModalCallbackRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        form.resetFields();
        setPendingFiles([]);
        setPendingLinks([]);

        setLinkModalOpen(false);
        linkModalCallbackRef.current = null;
        linkModalForm.resetFields();
        const md =
            typeof blocker?.description?.markdown === 'string'
                ? blocker.description.markdown
                : typeof blocker?.description === 'string'
                    ? blocker.description
                    : '';
        if (md) {
            form.setFieldValue('description', md);
        }
    }, [open, form, linkModalForm, blocker]);

    // Cleanup blob preview URLs
    useEffect(() => {
        return () => {
            pendingFiles.forEach((p) => {
                if (p?.preview && typeof p.preview === 'string' && p.preview.startsWith('blob:')) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        // ignore
                    }
                }
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const attachmentIndexOffset = pendingFiles.length;

    const handleFilesSelected = useCallback((files) => {
        const next = makePendingFileEntries(files);
        if (next.length === 0) return;
        setPendingFiles((prev) => [...prev, ...next]);
    }, []);

    const removePendingFile = useCallback(
        (id) => {
            setPendingFiles((prev) => {
                const index = prev.findIndex((p) => p.id === id);
                if (index < 0) return prev.filter((p) => p.id !== id);

                // Remove any anchor pointing to this attachment index and shift subsequent indices down.
                let content = form.getFieldValue('description') || '';
                const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
                content = content.replace(linkRegex, '');
                for (let j = prev.length - 1; j > index; j--) {
                    content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
                }
                form.setFieldValue('description', content);
                return prev.filter((p) => p.id !== id);
            });
        },
        [form],
    );

    const handleLinkRequest = useCallback(
        (insertLink) => {
            linkModalCallbackRef.current = insertLink;
            linkModalForm.resetFields();
            setLinkModalOpen(true);
        },
        [linkModalForm],
    );

    const handleLinkModalSubmit = useCallback(() => {
        linkModalForm.validateFields().then((values) => {
            const url = String(values?.url ?? '').trim();
            const title = String(values?.title ?? '').trim();
            if (!url) return;
            const insertLink = linkModalCallbackRef.current;
            if (typeof insertLink === 'function') {
                insertLink({ url, title: title || url });
            }
            setPendingLinks((prev) => [...prev, { id: `task-blocker-link-${Date.now()}`, url, title: title || url }]);
            setLinkModalOpen(false);
            linkModalCallbackRef.current = null;
        });
    }, [linkModalForm]);

    const removePendingLink = useCallback((id) => {
        setPendingLinks((prev) => prev.filter((p) => p.id !== id));
    }, []);

    const { createBlockerAsync, updateBlockerAsync, createBlockerPending, updateBlockerPending } = useProjectTaskBlockers(projectId, taskId);
    const isPending = createBlockerPending || updateBlockerPending;
    const canSubmit = useMemo(() => !!projectId && !!taskId && open && !isPending, [projectId, taskId, open, isPending]);

    const handleSubmit = useCallback(async () => {
        if (!canSubmit) return;
        try {
            const values = await form.validateFields();
            const html = contentToApiMentions(values?.description ?? '');
            const description = buildTaskDescription(html);
            const hasFiles = pendingFiles.length > 0;
            const linkAttachments = pendingLinks.map(({ url, title }) => ({ type: 'link', url, title }));
            const blockerId = blocker?.id;

            let finalBody;

            if (hasFiles) {
                const formData = new FormData();
                formData.append('description', JSON.stringify(description || { markdown: '' }));
                if (linkAttachments.length > 0) {
                    formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                }
                pendingFiles.forEach((p) => {
                    if (p?.file) formData.append('files', p.file);
                });
                finalBody = formData;
            } else {
                finalBody = {
                    description: description || { markdown: '' },
                    ...(linkAttachments.length > 0 ? { attachments: linkAttachments } : {}),
                };
            }

            if (blockerId) {
                await updateBlockerAsync({ blockerId, body: finalBody });
            } else {
                await createBlockerAsync(finalBody);
            }

            message.success(blockerId ? 'Blocker updated' : 'Blocker added');
            onCreated?.();
            onCancel?.();
        } catch (err) {
            if (err?.errorFields) return; // antd validation error
            message.error(err?.response?.data?.message || err?.message || (blocker?.id ? 'Failed to update blocker' : 'Failed to add blocker'));
        }
    }, [canSubmit, form, pendingFiles, pendingLinks, blocker?.id, updateBlockerAsync, createBlockerAsync, onCreated, onCancel]);

    return (
        <>
            <AppModal
                size="md"
                title={<ModalChromeHeading>{blocker?.id ? 'Edit blocker' : 'Add blocker'}</ModalChromeHeading>}
                open={open}
                onCancel={onCancel}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        <Button $primary={false} $size="14px" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button
                            $primary
                            $blackOnLight
                            $size="14px"
                            loading={isPending}
                            icon={<SendOutlined />}
                            onClick={handleSubmit}
                        >
                            {blocker?.id ? 'Save' : 'Add'}
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    <Form form={form} layout="vertical" requiredMark={false}>
                        <Form.Item
                            name="description"
                            label={<ModalBlockLabel>Description</ModalBlockLabel>}
                            rules={[{ required: true, message: 'Add a description' }]}
                        >
                            <RichTextEditor
                                placeholder="Describe what is blocking this task — bold, lists, links, @mentions. Attach files or links."
                                showAttachButton
                                isAttachment
                                isLink
                                isMention
                                mentionUsers={mentionUsers}
                                mentionMilestones={mentionMilestones}
                                mentionSprints={mentionSprints}
                                attachmentIndexOffset={attachmentIndexOffset}
                                onFilesSelected={handleFilesSelected}
                                onLinkRequest={handleLinkRequest}
                                onAttachmentRemoved={(index) => {
                                    const p = pendingFiles[index];
                                    if (p) removePendingFile(p.id);
                                }}
                            />
                        </Form.Item>

                        {pendingLinks.length > 0 && open && (
                            <PendingLinkWrap links={pendingLinks} onRemoveLink={removePendingLink} />
                        )}

                        {pendingFiles.length > 0 && (
                            <div
                                style={{
                                    marginTop: 8,
                                    marginBottom: 16,
                                    padding: '8px 0',
                                    borderTop: '1px solid var(--ant-color-border, #f0f0f0)',
                                }}
                            >
                                <PanelSectionLabel
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        marginBottom: 8,
                                        fontSize: 11,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                        opacity: 0.75,
                                    }}
                                >
                                    <FileOutlined />
                                    Attachments
                                </PanelSectionLabel>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                        gap: 10,
                                    }}
                                >
                                    {pendingFiles.map((p) => (
                                        <PendingAttachmentTile
                                            key={p.id}
                                            item={p}
                                            onRemove={() => removePendingFile(p.id)}
                                            truncateFileName={truncateFileName}
                                            showBottomFileName={false}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </Form>
                </CreateRoleModalBody>
            </AppModal>

            <AppModal
                size="sm"
                title={<ModalChromeHeading>Add link</ModalChromeHeading>}
                open={linkModalOpen}
                onCancel={() => {
                    setLinkModalOpen(false);
                    linkModalCallbackRef.current = null;
                }}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        <Button
                            $primary={false}
                            $size="14px"
                            onClick={() => {
                                setLinkModalOpen(false);
                                linkModalCallbackRef.current = null;
                            }}
                        >
                            Cancel
                        </Button>
                        <Button $primary $blackOnLight $size="14px" icon={<SendOutlined />} onClick={handleLinkModalSubmit}>
                            Add link
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    <Form form={linkModalForm} layout="vertical" requiredMark={false}>
                        <Form.Item noStyle shouldUpdate={(prev, next) => prev?.url !== next?.url}>
                            {() => {
                                const urlErrors = linkModalForm?.getFieldError?.('url') || [];
                                const showExample = urlErrors.length === 0;
                                return (
                                    <Form.Item
                                        name="url"
                                        label={<ModalBlockLabel>URL</ModalBlockLabel>}
                                        rules={[
                                            {
                                                validator: async (_rule, value) => {
                                                    const check = validateLinkUrlInput(value);
                                                    if (check.ok) return Promise.resolve();
                                                    return Promise.reject(new Error(check.message || 'Enter a valid URL'));
                                                },
                                            },
                                        ]}
                                        extra={showExample ? LINK_URL_EXAMPLE_HELP : null}
                                    >
                                        <Input
                                            placeholder="https://..."
                                            size="large"
                                            onChange={(e) => {
                                                const raw = e?.target?.value ?? '';
                                                const prev = linkModalForm?.getFieldValue?.('url');
                                                const normalized = normalizeUrlOnFirstType(raw, prev);
                                                if (normalized !== raw) {
                                                    linkModalForm?.setFieldsValue?.({ url: normalized });
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                );
                            }}
                        </Form.Item>
                        <Form.Item name="title" label={<ModalBlockLabel>Link text</ModalBlockLabel>}>
                            <Input placeholder="Optional display text" size="large" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
            </AppModal>
        </>
    );
}

